import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api } from '@/lib/api'
import type { User } from '@/types'
import type { AdminPermissionKey } from '@/constants'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (firstName: string, lastName: string, email: string, password: string) => Promise<void>
  logout: () => void
  hasPermission: (key: AdminPermissionKey) => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const loadMe = async (): Promise<User> => {
    const res = await api.admin.getMe()
    const userData = res.user as unknown as User
    const permissions = res.permissions ?? []
    return {
      ...userData,
      isSuperAdmin: userData.isSuperAdmin ?? false,
      permissions,
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      loadMe()
        .then((userData) => {
          if (userData.isSuperAdmin || userData.permissions.length > 0) {
            setUser(userData)
          } else {
            localStorage.removeItem('admin_token')
            setUser(null)
          }
        })
        .catch(() => {
          localStorage.removeItem('admin_token')
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const login = async (email: string, password: string) => {
    const res = await api.admin.login({ email, password })
    localStorage.setItem('admin_token', res.token)
    const userData = await loadMe()
    setUser(userData)
  }

  const register = async (firstName: string, lastName: string, email: string, password: string) => {
    const res = await api.admin.register({ firstName, lastName, email, password })
    localStorage.setItem('admin_token', res.token)
    const userData = await loadMe()
    setUser(userData)
  }

  const logout = () => {
    localStorage.removeItem('admin_token')
    setUser(null)
  }

  const hasPermission = useCallback(
    (key: AdminPermissionKey): boolean => {
      if (!user) return false
      if (user.isSuperAdmin) return true
      return user.permissions.includes(key)
    },
    [user]
  )

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
